import { PERIODONTAL_SITES, PERIODONTAL_SURFACES } from '../constants/odontology';
import type { PeriodontalMeasurements, PeriodontalSummary } from '../types/odontology';

/**
 * Índices periodontais agregados, calculados no cliente para pré-visualização.
 * O backend recalcula os mesmos valores ao salvar, e é ele a fonte de verdade.
 *
 * O nível de inserção clínica é PS menos a margem gengival, com margem negativa
 * representando recessão.
 */
export function computePeriodontalSummary(measurements: PeriodontalMeasurements): PeriodontalSummary {
  let teethExamined = 0;
  let sitesExamined = 0;
  let plaqueSites = 0;
  let bleedingSites = 0;
  let pdSum = 0;
  let calSum = 0;
  let calCount = 0;
  let sites45 = 0;
  let sites6 = 0;
  let mobilityTeeth = 0;
  let furcationTeeth = 0;

  Object.values(measurements || {}).forEach((tooth) => {
    if (!tooth || tooth.absent) return;

    let toothHasSite = false;

    PERIODONTAL_SURFACES.forEach((surface) => {
      const surfaceData = tooth[surface];
      if (!surfaceData) return;
      PERIODONTAL_SITES.forEach((site) => {
        const measurement = surfaceData[site];
        if (!measurement) return;
        const pd = Number(measurement.pd);
        if (!Number.isFinite(pd)) return;

        toothHasSite = true;
        sitesExamined += 1;
        pdSum += pd;
        if (pd >= 6) sites6 += 1;
        else if (pd >= 4) sites45 += 1;
        if (measurement.bop) bleedingSites += 1;
        if (measurement.plaque) plaqueSites += 1;

        const gm = Number(measurement.gm);
        if (Number.isFinite(gm)) {
          calSum += pd - gm;
          calCount += 1;
        }
      });
    });

    if (toothHasSite) teethExamined += 1;
    if (Number(tooth.mobility) > 0) mobilityTeeth += 1;
    if (Number(tooth.furcation) > 0) furcationTeeth += 1;
  });

  const pct = (part: number) => (sitesExamined ? Math.round((part / sitesExamined) * 1000) / 10 : 0);
  const avg = (sum: number, count: number) => (count ? Math.round((sum / count) * 10) / 10 : 0);

  return {
    teeth_examined: teethExamined,
    sites_examined: sitesExamined,
    plaque_index: pct(plaqueSites),
    bleeding_index: pct(bleedingSites),
    mean_probing_depth: avg(pdSum, sitesExamined),
    mean_attachment_loss: avg(calSum, calCount),
    sites_pd_4_5: sites45,
    sites_pd_6_plus: sites6,
    teeth_with_mobility: mobilityTeeth,
    teeth_with_furcation: furcationTeeth,
  };
}
